import { MessageSquare, History, Plus, Trash2, Menu, X } from 'lucide-react';
import type { ChatSession } from '@/types/dashboard';

export interface ChatSidebarProps {
  sessions: ChatSession[];
  currentSessionId: string | null;
  totalCount: number;
  showHistory: boolean;
  sidebarOpen: boolean;
  loadingMore: boolean;
  onToggleHistory: () => void;
  onToggleSidebar: (open: boolean) => void;
  onLoadSession: (id: string) => void;
  onDeleteSession: (id: string) => void;
  onNewChat: () => void;
  onLoadMore: () => void;
}

export default function ChatSidebar({
  sessions,
  currentSessionId,
  totalCount,
  showHistory,
  sidebarOpen,
  loadingMore,
  onToggleHistory,
  onToggleSidebar,
  onLoadSession,
  onDeleteSession,
  onNewChat,
  onLoadMore,
}: ChatSidebarProps) {
  return (
    <>
      {/* Mobile Header with hamburger menu */}
      <div className="lg:hidden shrink-0 bg-background border-b border-primary/30 px-4 py-2 flex items-center gap-3">
        <button
          onClick={() => onToggleSidebar(true)}
          className="p-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
          aria-label="Open sidebar"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-primary" />
          <span className="font-semibold text-foreground">Chat</span>
        </div>
        <button
          onClick={onNewChat}
          className="ml-auto p-2 bg-primary/20 text-primary border border-primary/30 rounded-lg hover:bg-primary/30 transition-colors"
          aria-label="New Chat"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black/50 backdrop-blur-xs"
          onClick={() => onToggleSidebar(false)}
          role="presentation"
        />
      )}

      {/* Sidebar */}
      <aside className={`
        w-80 border-r border-primary/30 glass-header flex flex-col shrink-0
        fixed lg:relative inset-y-0 left-0 z-50
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-bold bg-linear-to-r from-primary to-orange-400 bg-clip-text text-transparent">
                Chat
              </h2>
            </div>
            <button
              onClick={() => onToggleSidebar(false)}
              className="lg:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-primary/10 transition-colors"
              aria-label="Close sidebar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Agent and model are auto-configured -- no user selection needed */}

        </div>

        {/* Chat History */}
        <div className="flex-1 border-t border-primary/20 flex flex-col min-h-0">
          <div className="flex items-center justify-between p-4">
            <button
              onClick={onToggleHistory}
              className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary"
            >
              <History className="h-4 w-4" />
              Chat History ({totalCount})
            </button>
            <button
              onClick={onNewChat}
              className="p-1.5 bg-primary/20 text-primary border border-primary/30 rounded-lg hover:bg-primary/30 transition-colors"
              aria-label="New Chat"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
          {showHistory && (
            <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
              {sessions.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">
                  No previous chats
                </p>
              ) : (
                <>
                  {sessions.map((session) => (
                    <div
                      key={session.id}
                      className={`group relative p-3 rounded-lg cursor-pointer transition-colors ${
                        currentSessionId === session.id
                          ? 'bg-primary/20 border border-primary/40'
                          : 'bg-card/50 border border-primary/10 hover:border-primary/30'
                      }`}
                      onClick={() => onLoadSession(session.id)}
                    >
                      <p className="text-sm text-foreground truncate pr-6">{session.title}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(session.updated_at).toLocaleDateString()} · {session.message_count} msgs
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteSession(session.id);
                        }}
                        className="absolute top-2 right-2 p-1 opacity-0 group-hover:opacity-100 text-destructive hover:bg-destructive/20 rounded transition-all"
                        aria-label={`Delete chat: ${session.title}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  {sessions.length < totalCount && (
                    <button
                      onClick={onLoadMore}
                      disabled={loadingMore}
                      className="w-full py-2 text-xs text-primary hover:text-primary/80 hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {loadingMore ? 'Loading...' : `Load more (${sessions.length} of ${totalCount})`}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
